// pages/Dashboard/LinkDetail.jsx - Link detail page with submission history
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import dashboardService from '../../services/dashboardService';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

const statusColors = {
  'completed': 'bg-green-100 text-green-800',
  'pending': 'bg-yellow-100 text-yellow-800',
  'expired': 'bg-gray-100 text-gray-800',
  'revoked': 'bg-red-100 text-red-800'
};

export default function LinkDetail() {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  useEffect(() => {
    fetchLinkDetail();
  }, [shortCode]);

  const fetchLinkDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getLink(shortCode);
      setLinkData(data);
    } catch (err) {
      console.error('Failed to fetch link detail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeLink = async () => {
    if (!window.confirm('Are you sure you want to revoke this link? This action cannot be undone.')) {
      return;
    }

    try {
      await dashboardService.revokeLink(shortCode);
      toast.success('Link revoked successfully');
      fetchLinkDetail();
    } catch (err) {
      console.error('Failed to revoke link:', err);
      toast.error(err.message || 'Failed to revoke link');
    }
  };

  const handleResendPDF = async () => {
    try {
      toast.loading('Resending PDF...', { id: 'resend-pdf' });
      await dashboardService.resendPDF(shortCode);
      toast.success('PDF resent successfully', { id: 'resend-pdf' });
    } catch (err) {
      console.error('Failed to resend PDF:', err);
      toast.error(err.message || 'Failed to resend PDF', { id: 'resend-pdf' });
    }
  };

  const handleCopyUrl = () => {
    const url = `${window.location.origin}/${shortCode}`;
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const renderFormData = (data) => {
    if (!data) return null;

    return (
      <div className="space-y-4">
        {/* Crew Information */}
        {data.crew && data.crew.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Crew Members</h5>
            <div className="flex flex-wrap gap-2">
              {data.crew.map((member, idx) => (
                <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {member.name} ({member.role})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hazards */}
        {data.hazards && data.hazards.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Identified Hazards</h5>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              {data.hazards.map((hazard, idx) => (
                <li key={idx}>{hazard}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Difficulty Ratings */}
        {(data.climbingDifficulty || data.riggingDifficulty) && (
          <div className="grid grid-cols-2 gap-4">
            {data.climbingDifficulty && (
              <div>
                <h5 className="text-sm font-medium text-gray-700">Climbing Difficulty</h5>
                <div className="flex items-center mt-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(data.climbingDifficulty / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600">{data.climbingDifficulty}/5</span>
                </div>
              </div>
            )}
            {data.riggingDifficulty && (
              <div>
                <h5 className="text-sm font-medium text-gray-700">Rigging Difficulty</h5>
                <div className="flex items-center mt-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(data.riggingDifficulty / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600">{data.riggingDifficulty}/5</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Weather */}
        {data.weather && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-1">Weather Conditions</h5>
            <p className="text-sm text-gray-600">
              {data.weather.condition}, {data.weather.temperature}
              {data.weather.wind && `, Wind: ${data.weather.wind}`}
            </p>
          </div>
        )}

        {/* Notes */}
        {data.notes && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-1">Notes</h5>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{data.notes}</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="bg-white shadow rounded-lg p-6">
            <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading link details</h3>
            <p className="mt-2 text-sm text-red-700">{error}</p>
            <div className="mt-4">
              <Link
                to="/dashboard/links"
                className="text-sm font-medium text-red-600 hover:text-red-500"
              >
                Back to Form Links
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { link, submissions } = linkData || {};

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/dashboard/links" className="text-gray-400 hover:text-gray-500">
              Form Links
            </Link>
          </li>
          <li>
            <svg className="flex-shrink-0 h-5 w-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li>
            <span className="text-gray-500">{link?.job_number || shortCode}</span>
          </li>
        </ol>
      </nav>

      {/* Link metadata */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {link?.job_number ? `Job #${link.job_number}` : 'Form Link Details'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">{link?.client_name || 'Unknown client'}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColors[link?.status] || 'bg-gray-100 text-gray-800'}`}>
              {link?.status?.charAt(0).toUpperCase() + link?.status?.slice(1)}
            </span>
          </div>
        </div>

        <div className="px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Short Code</dt>
              <dd className="mt-1 text-sm text-gray-900 font-mono">{shortCode}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(link?.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Expires</dt>
              <dd className="mt-1 text-sm text-gray-900">{link?.expires_at ? formatDate(link.expires_at) : 'Never'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Submissions</dt>
              <dd className="mt-1 text-sm text-gray-900">{submissions?.length || 0}</dd>
            </div>
          </dl>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={handleCopyUrl}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy URL
            </button>

            {link?.status === 'completed' && (
              <button
                onClick={handleResendPDF}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Resend PDF
              </button>
            )}

            {link?.status !== 'revoked' && link?.status !== 'expired' && (
              <button
                onClick={handleRevokeLink}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Revoke Link
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Submission history */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Submission History
          </h3>
        </div>

        {submissions && submissions.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {submissions.map((submission, index) => (
              <div key={index} className="px-4 py-4 sm:px-6">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedSubmission(expandedSubmission === index ? null : index)}
                >
                  <div className="flex items-center">
                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-green-100">
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        Submission #{index + 1}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(submission.submitted_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {submission.form_data?.hazards?.length > 0 && (
                      <span className="mr-4 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {submission.form_data.hazards.length} hazard{submission.form_data.hazards.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <svg
                      className={`h-5 w-5 text-gray-400 transform transition-transform ${expandedSubmission === index ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded form data */}
                {expandedSubmission === index && (
                  <div className="mt-4 ml-14 pl-4 border-l-2 border-gray-200">
                    {renderFormData(submission.form_data)}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              The form has not been submitted yet.
            </p>
          </div>
        )}
      </div>

      {/* Back button */}
      <div>
        <Link
          to="/dashboard/links"
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Form Links
        </Link>
      </div>
    </div>
  );
}
